import { useEffect, useRef, useState } from 'react'
import { useTranslation } from '../i18n'
import { COLORS } from '../lib/theme'
import { Icon } from './Icon'
import { Switch } from './Switch'

export interface ReportSharingCardProps {
  /**
   * The server's confirmed state — the single source of truth for what the toggle
   * shows. The card never keeps its own copy: the parent re-renders with a fresh
   * value once the invalidated query answers.
   */
  shared: boolean
  participantNames: string[]
  /**
   * Persist the new value. Resolves when the server has confirmed it, or rejects
   * (the card surfaces the error and leaves the displayed state untouched).
   */
  onToggle: (next: boolean) => Promise<void>
}

/**
 * The "What participants can see" card on an ended estate hunt: one toggle that
 * shares the report and replay with everyone who took part, plus an info popover
 * explaining what is shared, who receives it, and that it can be turned off again.
 *
 * Presentation only: the state and the persist come in as props, so this holds no
 * API knowledge and the domain wires the hook.
 *
 * The toggle is SERVER-CONFIRMED, never optimistic. It renders the `shared` prop
 * as-is; on change it awaits `onToggle`, disabling the switch while the request is
 * in flight, and on failure it shows an error without flipping the displayed state.
 * The team previously shipped an optimistic version of this that showed a success
 * the server had not granted — reflecting the prop is what prevents that.
 */
export function ReportSharingCard({ shared, participantNames, onToggle }: ReportSharingCardProps) {
  const { t } = useTranslation('huntInvitation')
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [infoOpen, setInfoOpen] = useState(false)
  const infoRef = useRef<HTMLDivElement>(null)

  const count = participantNames.length
  const recipientLine = t('reportSharing.recipients', { count, names: participantNames.join(', ') })

  // Close the popover on Escape or an outside click — a hand-rolled popover gets
  // neither for free, and a keyboard user would otherwise be unable to dismiss it.
  useEffect(() => {
    if (!infoOpen) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setInfoOpen(false) }
    const onClick = (e: MouseEvent) => {
      if (infoRef.current && !infoRef.current.contains(e.target as Node)) setInfoOpen(false)
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('mousedown', onClick)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('mousedown', onClick)
    }
  }, [infoOpen])

  const toggle = async (next: boolean) => {
    setPending(true); setError(null)
    try {
      await onToggle(next)
    } catch (e) {
      // The domain container translates a known code (estate-only, hunt-not-ended)
      // into a localized message before it reaches here; anything else falls back to
      // the generic error. The displayed state stays on the prop, so a rejected
      // toggle never reads as success.
      setError(e instanceof Error && e.message ? e.message : t('reportSharing.error'))
    } finally {
      setPending(false)
    }
  }

  return (
    <div className="wh-card" style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.onSurface }}>{t('reportSharing.cardTitle')}</div>
        <div style={{ position: 'relative' }} ref={infoRef}>
          <button
            className="wh-btn-icon"
            aria-label={t('reportSharing.infoLabel')}
            aria-expanded={infoOpen}
            onClick={() => setInfoOpen((v) => !v)}
          >
            <Icon name="info" size={16} />
          </button>
          {infoOpen && (
            <div
              role="dialog"
              aria-label={t('reportSharing.infoTitle')}
              style={{
                position: 'absolute', top: '100%', left: 0, zIndex: 100, marginTop: 6, width: 260,
                background: COLORS.surfaceContainerLowest, border: `1px solid ${COLORS.surfaceContainerHighest}`,
                borderRadius: 10, padding: 14, boxShadow: '0 6px 24px rgba(0,0,0,0.18)',
                display: 'flex', flexDirection: 'column', gap: 10,
              }}
            >
              <InfoSection heading={t('reportSharing.whatSharedHeading')} body={t('reportSharing.whatSharedBody')} />
              <InfoSection heading={t('reportSharing.whoGetsHeading')} body={t('reportSharing.whoGetsBody')} />
              <InfoSection heading={t('reportSharing.turningOffHeading')} body={t('reportSharing.turningOffBody')} />
            </div>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, color: COLORS.onSurface }}>{t('reportSharing.toggleLabel')}</div>
          <div style={{ fontSize: 12, color: COLORS.outline }}>{t('reportSharing.toggleSubtext')}</div>
        </div>
        <Switch on={shared} onChange={toggle} disabled={pending} aria-label={t('reportSharing.toggleLabel')} />
      </div>

      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, color: COLORS.onSurfaceVariant }}>
        <Icon name="user" size={15} />
        <span>{recipientLine}</span>
      </div>

      {pending && <div style={{ fontSize: 12, color: COLORS.outline }}>{t('reportSharing.pending')}</div>}
      {error && <div role="alert" style={{ color: COLORS.error, fontSize: 12 }}>{error}</div>}
    </div>
  )
}

const heading: React.CSSProperties = {
  fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: COLORS.outline, marginBottom: 2,
}

function InfoSection({ heading: text, body }: { heading: string; body: string }) {
  return (
    <div>
      <div style={heading}>{text}</div>
      <div style={{ fontSize: 12, color: COLORS.onSurfaceVariant, lineHeight: 1.4 }}>{body}</div>
    </div>
  )
}
